import { FC, JSX, useState, useRef } from 'react';
import {
  MenuItem,
  ListItemContent,
  Typography,
  Menu,
  Avatar,
  Box,
} from '@mui/joy';
import { Business, KeyboardArrowDown } from '@mui/icons-material';
import useUserPermissions, {
  SELECTED_USER_ROLE_KEY,
} from '@consolidocs-ui/hooks/useUserPermissions';

const DepartmentSelector: FC = (): JSX.Element => {
  const { roles, selectedRoleKey } = useUserPermissions();
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleChange = (value: string) => {
    if (!value) return;
    localStorage.setItem(SELECTED_USER_ROLE_KEY, value);
    window.location.href = '/';
  };

  if (!roles || roles.length < 2) return <></>;

  const selectedRole = roles.find(
    (role) => `${role.role_id}/${role.department_id}` === selectedRoleKey
  );
  const selectedLabel = selectedRole
    ? `${selectedRole.department} (${selectedRole.role})`
    : 'Choose Department';

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
    setSubmenuOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setSubmenuOpen(false), 200);
  };

  return (
    <>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: { xs: 0, sm: 200 },
          maxWidth: { xs: '100%', sm: 'auto' },
          flexShrink: 1,
          gap: { xs: 0.5, sm: 0.9 },
          padding: { xs: 0.5, sm: 1 },
          paddingX: { xs: 0.5, sm: 1 },
          paddingY: { xs: 0, sm: 1 },
          borderRadius: 'sm',
          border: '0.2px solid',
          borderColor: 'white',
        }}
      >
        <Avatar
          variant="outlined"
          size="sm"
          sx={{
            '--Avatar-size': { xs: '12px', sm: '14px' },
            bgcolor: 'transparent',
            color: 'white',
            p: 0.5,
            flexShrink: 0,
          }}
        >
          <Business
            fontSize="small"
            sx={{ fontSize: { xs: '12px', sm: '14px' }, color: 'white' }}
          />
        </Avatar>
        <ListItemContent
          sx={{
            minWidth: 0,
            flex: { xs: 1, sm: 'unset' },
          }}
        >
          <Typography
            level="body-sm"
            sx={{
              fontWeight: 500,
              color: 'white',
              fontSize: { xs: '11px', sm: '13px' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '120px', sm: 'none' },
            }}
          >
            {selectedLabel}
          </Typography>
        </ListItemContent>
        <KeyboardArrowDown
          fontSize="small"
          sx={{ fontSize: { xs: '12px', sm: '14px' }, color: 'white', flexShrink: 0 }}
        />
      </Box>

      <Menu
        open={submenuOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        onMouseEnter={(e) => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setSubmenuOpen(true);
        }}
        onMouseLeave={handleMouseLeave}
        sx={{ zIndex: 1500, minWidth: { xs: 160, sm: 220 } }}
      >
        {roles.map((role) => {
          const value = `${role.role_id}/${role.department_id}`;
          return (
            <MenuItem
              key={value}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleChange(value);
              }}
              selected={selectedRoleKey === value}
            >
              <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                {role.department} ({role.role})
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default DepartmentSelector;
